import { browserIsMac } from "@redotech/web-util/browser";

export type KeyboardModifier = "meta" | "ctrl" | "alt" | "shift";

/**
 * A keyboard shortcut definition.
 * Uses physical key codes for cross-platform compatibility.
 *
 * @template TScope - The scope type for this shortcut (e.g., string literal union)
 */
export interface KeyboardShortcut<TScope extends string = string> {
  /** The physical key code (e.g., "KeyC", "Enter", "Space") - used for matching */
  code: string;
  /** Human readable label for display (e.g., "C", "Enter", "Space") */
  keyLabel: string;
  /** Modifier keys required. "meta" maps to Cmd on Mac, Ctrl on Windows/Linux */
  modifiers?: readonly KeyboardModifier[];
  /** The scope where this shortcut is active */
  scope?: TScope;
}

/**
 * Check if a keyboard event matches a shortcut definition.
 */
export function matchesShortcut(
  event: KeyboardEvent,
  shortcut: KeyboardShortcut,
): boolean {
  const isMac = browserIsMac();
  const modifiers = shortcut.modifiers ?? [];

  // "meta" maps to Cmd on Mac, Ctrl on Windows/Linux
  const metaPressed = isMac ? event.metaKey : event.ctrlKey;
  // "ctrl" is always Ctrl key regardless of platform
  const ctrlPressed = isMac ? event.ctrlKey : false;

  return (
    event.code === shortcut.code &&
    modifiers.includes("meta") === metaPressed &&
    modifiers.includes("ctrl") === ctrlPressed &&
    modifiers.includes("alt") === event.altKey &&
    modifiers.includes("shift") === event.shiftKey
  );
}

/**
 * Format a keyboard shortcut for display (e.g., "⌘C" or "Ctrl+C")
 */
export function formatShortcut(shortcut: KeyboardShortcut): string {
  const isMac = browserIsMac();

  const parts: string[] = [];
  const modifiers = shortcut.modifiers ?? [];

  if (modifiers.includes("meta")) {
    parts.push(isMac ? "⌘" : "Ctrl");
  }
  if (modifiers.includes("ctrl")) {
    parts.push("Ctrl");
  }
  if (modifiers.includes("alt")) {
    parts.push(isMac ? "⌥" : "Alt");
  }
  if (modifiers.includes("shift")) {
    parts.push(isMac ? "⇧" : "Shift");
  }

  parts.push(shortcut.keyLabel);

  return parts.join(isMac ? "" : "+");
}

/**
 * Get shortcut display parts as an array (for keyboard shortcut components).
 */
export function getShortcutDisplayParts(shortcut: KeyboardShortcut): string[] {
  const isMac = browserIsMac();

  const parts: string[] = [];
  const modifiers = shortcut.modifiers ?? [];

  if (modifiers.includes("meta")) {
    parts.push(isMac ? "⌘" : "Ctrl");
  }
  if (modifiers.includes("ctrl")) {
    parts.push("Ctrl");
  }
  if (modifiers.includes("alt")) {
    parts.push(isMac ? "⌥" : "Alt");
  }
  if (modifiers.includes("shift")) {
    parts.push(isMac ? "⇧" : "Shift");
  }

  parts.push(shortcut.keyLabel);

  return parts;
}

/**
 * Check if a keyboard event should be handled based on the event target.
 * Returns false if user is typing in an input/textarea/contenteditable.
 */
export function shouldHandleKeyboardEvent(event: KeyboardEvent): boolean {
  const target = event.target;

  if (
    target instanceof HTMLInputElement ||
    target instanceof HTMLTextAreaElement
  ) {
    return false;
  }

  if (target instanceof HTMLElement && target.isContentEditable) {
    return false;
  }

  return true;
}

/**
 * Create a unique string key from a shortcut for Map lookup.
 * Includes scope if provided.
 */
export function getShortcutKey(
  shortcut: KeyboardShortcut,
  scope?: string,
): string {
  const modifiers = [...(shortcut.modifiers ?? [])].sort().join("+");
  const scopePrefix = scope ?? shortcut.scope ?? "";
  return `${scopePrefix}:${modifiers}:${shortcut.code}`;
}

/**
 * Extract a shortcut key string from a keyboard event for Map lookup.
 */
export function getEventShortcutKey(
  event: KeyboardEvent,
  scope: string,
): string {
  const isMac = browserIsMac();

  const modifiers: string[] = [];
  if (event.altKey) {
    modifiers.push("alt");
  }
  if (isMac ? event.metaKey : event.ctrlKey) {
    modifiers.push("meta");
  }
  if (isMac && event.ctrlKey) {
    modifiers.push("ctrl");
  }
  if (event.shiftKey) {
    modifiers.push("shift");
  }

  modifiers.sort();
  return `${scope}:${modifiers.join("+")}:${event.code}`;
}
